/**
 * SvelteKit-aware adapter over the framework-agnostic `routePaths` registry.
 *
 * Each entry's `href()` returns `ResolvedPathname` (the brand type
 * `eslint-plugin-svelte`'s `no-navigation-without-resolve` rule recognises
 * via type-based detection), so call sites can do:
 *
 *     goto(routes.dashboard.href(auth));
 *     <a href={routes.home.href()}>Home</a>
 *
 * with no `resolve()` wrapper and no eslint-disable comment.
 *
 * This file is Svelte-only — it imports from `$app/paths` and `$app/types`.
 * Non-Svelte consumers (server-side path building, share-link generation,
 * schema metadata) should import `routePaths` from `./app-routes` instead.
 *
 * **Migration status (as of 2026-05-06):** proof scope. Three routes are
 * Svelte-adapted (`home`, `dashboard`, `signIn` — covering the no-args /
 * params / query patterns). The rest fall through via the `...routePaths`
 * spread and still return plain `string`. Migrate routes incrementally by
 * adding overrides below; each override unblocks dropping the
 * `eslint-disable-next-line svelte/no-navigation-without-resolve` comments
 * at that route's call sites.
 */

import type { AuthState } from '$lib/firebase/auth/auth.svelte';
import { resolve } from '$app/paths';
import type { ResolvedPathname } from '$app/types';
import { authToHandle } from './app-helpers';
import { routePaths } from './app-routes';

export const routes = {
  ...routePaths,

  home: { href: (): ResolvedPathname => resolve('/') },

  dashboard: {
    href: (authOrHandle: AuthState | string): ResolvedPathname =>
      resolve('/@[handle]', { handle: authToHandle(authOrHandle) }),
  },

  signIn: {
    href: (params?: { returnTo?: string; email?: string } | string): ResolvedPathname => {
      // Backwards-compat: treat a bare string as returnTo (existing call sites).
      const query = typeof params === 'string' ? { returnTo: params } : (params ?? {});
      const path = resolve('/sign-in');
      const parts: string[] = [];
      if (query.returnTo) parts.push(`returnTo=${encodeURIComponent(query.returnTo)}`);
      if (query.email) parts.push(`email=${encodeURIComponent(query.email)}`);
      const qs = parts.join('&');
      // Casting at the registry boundary — query-bearing routes can't go
      // through `resolve()` directly (it takes route ids, not paths with
      // queries), but the result is structurally a resolved pathname for
      // the no-navigation-without-resolve rule's bidirectional check.
      return (qs ? `${path}?${qs}` : path) as ResolvedPathname;
    },
  },
};
